/**
 * dexScanSelfCorrection — the reverse ratchet for the Dex live scanner.
 *
 * WHY
 * ---
 * The scanner's writes were one-directional: a catch, once applied, stood
 * forever. So a single bad frame (see the flat-cell false write on #91) silently
 * corrupted a cell and the only cure was the user noticing and fixing it by hand.
 *
 * This module makes the scanner self-healing. It watches for the scanner
 * CONTRADICTING its own earlier catch — the same cell read as not-caught, at
 * high confidence, over and over — and once the evidence is overwhelming it says
 * so, and the caller retracts the write (dexStateStore.retractScanPresent).
 *
 * DESIGN NOTES
 * ------------
 *  - Evidence must be OVERWHELMING, never incidental. One contrary frame means
 *    nothing (mid-scroll smear reads not-caught all the time); the threshold is
 *    deliberately high so only a sustained, confident disagreement can undo a
 *    catch. Getting this wrong in the loose direction re-introduces the exact
 *    false-negative problem the conservative scan design exists to avoid.
 *  - A single supporting read (the cell reads CAUGHT again) RESETS the counter
 *    outright. Suspicion has to be unbroken to count.
 *  - PURE: no store access, no globals. The caller supplies `isScanPresent` so
 *    only cells the scanner itself wrote can ever accumulate evidence, and this
 *    module stays trivially testable.
 */
import type { CellState } from "./pogoPokedexVariantClassifier";

/**
 * Contrary observations required before a scan-written catch is retracted.
 *
 * Set to 100 per the product call: "100 frames saying with +90% confidence that
 * it shouldn't have applied". NOTE the practical tradeoff — a cell is typically
 * only on screen for ~5–20 frames per pass, so 100 implies real dwell or several
 * passes over the same rows. If in device testing this proves so strict it never
 * fires, this is the single number to lower (~20–30 is still strong evidence).
 */
export const RETRACTION_EVIDENCE_THRESHOLD = 100;

/**
 * BLANK-CARD path (2026-09-24 owner direction: auto-correct). A catch is
 * undone, whatever wrote it (manual ticks included), when the scanner keeps
 * judging the card blank from the band of the CARD directly above the number
 * Vision found (pokedexOcr cardBandAboveNumber, `numberAnchored`). That band
 * is pinned to the card, not the lattice: on a caught card it is the sprite,
 * on a blank card the card's own empty upper half, where every blank measured
 * chroma exactly 0 across 9 frames x 8 lattice offsets. Binary dexes only
 * (Lucky/Perfect/XXL/XXS), where that band is read.
 *
 * NOT the number's position: the first cut keyed on a CENTRED number, and the
 * harness then showed 36 of 63 caught cards read "center" even at the real
 * device lattice; real catches (#13, #16, #19, #25, #33, #50, #52, #133) were
 * retracted and re-applied on device. Any caught read resets the run.
 */
export const BLANK_CARD_RETRACTION_THRESHOLD = 6;

/**
 * Independence rules for the blank-card path (2026-09-24 second device pass:
 * real catches #13/#16/#19/#25/#33/#50/#52/#133 were retracted, then
 * re-applied minutes later). Two things made one bad read look like six:
 *  - a CACHED replay (the cell's pixels did not change, so the previous
 *    classification is replayed) is the same read again, never new evidence;
 *  - a lattice registered half a cell off puts a caught card's bottom number
 *    in the middle of the misaligned cell, but only while the card sits at
 *    that screen position. A truly blank card reads blank wherever it sits.
 * So only fresh reads count, and they must come from this many different
 * on-screen rows.
 */
export const BLANK_CARD_MIN_DISTINCT_ROWS = 2;

/** Minimum per-cell confidence for a contrary read to count at all. */
export const RETRACTION_MIN_CONFIDENCE = 0.9;

/** The subset of a cell observation this tracker needs. */
export interface SelfCorrectionObservation {
  dexNumber: number | null | undefined;
  state: CellState;
  /** Classifier confidence for this cell, 0..1. */
  spriteConfidence?: number;
  /** Pixel-classifier structural state, when known. */
  spriteState?: string;
  /** Where the dex number sat in the card (diagnostic only here). */
  numberPosition?: string;
  /** Caught/blank judged from the card band above the Vision number box. */
  numberAnchored?: boolean;
  /** A replay of an earlier classification of unchanged pixels. */
  classificationCached?: boolean;
  /** Vertical screen position of the cell's row (a row key). */
  yPct?: number;
}

export interface SelfCorrectionConfig {
  threshold?: number;
  minConfidence?: number;
  blankCardThreshold?: number;
}

export interface SelfCorrectionTracker {
  /**
   * Feed one frame's observations. Returns the dex numbers whose evidence has
   * now crossed the threshold and which the caller should retract. Counters for
   * returned ids are cleared, so a retraction is emitted once, not every frame.
   */
  observe(
    observations: readonly SelfCorrectionObservation[],
    isScanPresent: (dexNumber: number) => boolean,
    /** Present in the dex from ANY source (manual included): the blank-card
     *  path's gate. Omitted = the blank-card path is off. */
    isPresentAnySource?: (dexNumber: number) => boolean,
  ): number[];
  /** Evidence count for a cell — diagnostics/tests. */
  countFor(dexNumber: number): number;
  /** Drop all evidence (new session / variant switch). */
  reset(): void;
}

/**
 * True when an observation is high-confidence evidence the cell is NOT caught.
 * A "flat empty" structural read counts even without a confidence number — that
 * is the featureless-cell signal the flat-cell veto keys on, and it is not a
 * judgement call.
 */
function isContrary(
  obs: SelfCorrectionObservation,
  minConfidence: number,
): boolean {
  if (obs.state !== "missing") return false;
  if (obs.spriteState === "empty") return true;
  return (obs.spriteConfidence ?? 0) >= minConfidence;
}

/** Create a tracker. One per scan session (evidence must not leak across scans). */
export function createSelfCorrectionTracker(
  config: SelfCorrectionConfig = {},
): SelfCorrectionTracker {
  const threshold = config.threshold ?? RETRACTION_EVIDENCE_THRESHOLD;
  const minConfidence = config.minConfidence ?? RETRACTION_MIN_CONFIDENCE;
  const blankThreshold = config.blankCardThreshold ?? BLANK_CARD_RETRACTION_THRESHOLD;
  const evidence = new Map<number, number>();
  const blankEvidence = new Map<number, { count: number; rows: Set<number> }>();

  return {
    observe(observations, isScanPresent, isPresentAnySource) {
      const retract: number[] = [];
      for (const obs of observations) {
        const id = obs.dexNumber;
        if (id == null || !Number.isFinite(id)) continue;

        // The cell reads CAUGHT again — the scanner agrees with itself, so any
        // accumulated suspicion is void. Unbroken disagreement only.
        if (obs.state === "present") {
          evidence.delete(id);
          blankEvidence.delete(id);
          continue;
        }
        // Blank-card path: the card band above the number reads blank.
        if (isPresentAnySource && obs.numberAnchored === true) {
          if (!isPresentAnySource(id)) {
            blankEvidence.delete(id);
          } else if (obs.classificationCached !== true) {
            const ev = blankEvidence.get(id) ?? { count: 0, rows: new Set<number>() };
            ev.count += 1;
            if (typeof obs.yPct === "number") ev.rows.add(Math.round(obs.yPct * 100));
            if (ev.count >= blankThreshold && ev.rows.size >= BLANK_CARD_MIN_DISTINCT_ROWS) {
              blankEvidence.delete(id);
              evidence.delete(id);
              retract.push(id);
              continue;
            }
            blankEvidence.set(id, ev);
          }
        }
        if (!isContrary(obs, minConfidence)) continue;
        // Only cells THIS scanner wrote as caught can be retracted; everything
        // else (manual edits, never-written cells) is none of our business.
        if (!isScanPresent(id)) {
          evidence.delete(id);
          continue;
        }
        const next = (evidence.get(id) ?? 0) + 1;
        if (next >= threshold) {
          evidence.delete(id);
          retract.push(id);
        } else {
          evidence.set(id, next);
        }
      }
      return retract;
    },
    countFor(dexNumber) {
      return Math.max(evidence.get(dexNumber) ?? 0, blankEvidence.get(dexNumber)?.count ?? 0);
    },
    reset() {
      evidence.clear();
      blankEvidence.clear();
    },
  };
}
